<template>
  <app-page-gradient-background class="pt-4 md:pt-8">
    <app-page-wrapper>
      <event-page-breadcrumb :event="data" />

      <div
        class="grid gap-8 lg:grid-cols-[auto_320px] xl:grid-cols-[auto_360px]"
      >
        <div>
          <event-page-content-wrapper class="py-6 md:py-11">
            <event-page-header :event="data" />
          </event-page-content-wrapper>

          <app-card class="!rounded-none md:!rounded-lg">
            <event-page-replay-cover
              v-if="data.replay"
              :event="data"
              :replay="data.replay"
            />
            <course-page-cover
              v-else
              class="rounded-lg"
              :course="data.course"
              :teacher="data.teacher"
            />

            <event-page-content-wrapper class="gap-sections grid py-16">
              <app-page-subsection
                v-if="data.course.description"
                :title="{
                  tag: 'h3',
                  text: $t('course.page.description_title'),
                }"
              >
                <app-text-clamped :max-lines="6">
                  <app-text-markdown
                    class="prose text-subtle max-w-[900px]"
                    lazy
                    :value="data.course.description"
                  />
                </app-text-clamped>
              </app-page-subsection>

              <course-page-audience
                :course="data.course"
                title="À qui s'adresse cet événement ?"
              />

              <course-page-outcomes :course="data.course" />

              <app-page-subsection
                v-if="data.course.program.length > 0"
                id="program"
                :title="{ tag: 'h3', text: $t('course.page.program_title') }"
              >
                <course-page-program-content
                  :course="data.course"
                  data-cy-program-content
                />
              </app-page-subsection>

              <course-page-teacher-details
                v-if="data.teacher"
                :teacher="data.teacher"
              />

              <app-page-subsection
                v-else
                :title="{ tag: 'h3', text: 'À propos de nos speakers' }"
              >
                <div class="grid gap-4 md:grid-cols-3">
                  <teacher-card-profile
                    v-for="teacher in data.teachers"
                    :key="teacher.id"
                    class="aspect-[1.7] md:aspect-[0.7]"
                    :teacher="teacher"
                  />
                </div>
              </app-page-subsection>
            </event-page-content-wrapper>
          </app-card>
        </div>

        <div>
          <div class="sticky-below-header">
            <event-page-replay-panel v-if="data.replay" :event="data" />
            <event-page-application-panel
              v-else-if="isUpcomingEvent"
              :event="data"
            />
          </div>
        </div>
      </div>

      <event-page-other-events class="mt-16 md:mt-24" :event="data" />
    </app-page-wrapper>
  </app-page-gradient-background>
</template>

<script lang="ts" setup>
import { isFuture } from "date-fns";

import {
  AUTO_REPLAY_OPENING_DELAY_IN_MS,
  POST_VIEW_REPLAY_REQUEST_QUERY_PARAMETER_KEY,
} from "~/business-areas/event/composables/event-replay.hook";
import { useEventView } from "~/business-areas/event/composables/event-view.hook";
import CoursePageAudience from "~/pages/courses/[slug]/_includes/components/course-page-audience.vue";
import CoursePageCover from "~/pages/courses/[slug]/_includes/components/course-page-cover.vue";
import CoursePageOutcomes from "~/pages/courses/[slug]/_includes/components/course-page-outcomes.vue";
import CoursePageTeacherDetails from "~/pages/courses/[slug]/_includes/components/course-page-teacher-details.vue";
import CoursePageProgramContent from "~/pages/courses/[slug]/_includes/components/program/course-page-program-content.vue";
import EventPageApplicationPanel from "~/pages/events/[slug]/_includes/components/event-page-application-panel.vue";
import EventPageBreadcrumb from "~/pages/events/[slug]/_includes/components/event-page-breadcrumb.vue";
import EventPageContentWrapper from "~/pages/events/[slug]/_includes/components/event-page-content-wrapper.vue";
import EventPageHeader from "~/pages/events/[slug]/_includes/components/event-page-header.vue";
import EventPageOtherEvents from "~/pages/events/[slug]/_includes/components/event-page-other-events.vue";
import EventPageReplayCover from "~/pages/events/[slug]/_includes/components/event-page-replay-cover.vue";
import EventPageReplayPanel from "~/pages/events/[slug]/_includes/components/event-page-replay-panel.vue";
import { useEventPageMetadata } from "~/pages/events/[slug]/_includes/composables/event-page-metadata.hook";

definePageMeta({
  hideQualiopiFooter: true,
  transparentHeader: true,
});

const slug = useRoute("events-slug").params.slug;

const { data } = await useEventView(slug);

useEventPageMetadata(data);

const isUpcomingEvent = computed(() =>
  isFuture(new Date(data.value.end_date!)),
);

const route = useRoute();
const { view } = useEventReplay(data.value);

onMounted(async () => {
  if (
    import.meta.client &&
    route.query[POST_VIEW_REPLAY_REQUEST_QUERY_PARAMETER_KEY] === "true"
  ) {
    await timer(AUTO_REPLAY_OPENING_DELAY_IN_MS);

    view();
  }
});
</script>
